import React, { useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Shield, Lock, Eye, Database, UserCheck } from 'lucide-react';

export default function RGPD() {
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (contentRef.current) {
      contentRef.current.scrollIntoView({ block: 'start', behavior: 'instant' });
    }
  }, []);

  return (
    <>
      <Helmet>
        <title>Politique de Protection des Données | Elite Transfert</title>
        <meta name="description" content="Politique de protection des données (RGPD) d'Elite Transfert - Service VTC de luxe à Paris" />
        <link rel="canonical" href="https://elite-transfer.fr/rgpd" />
      </Helmet>
      
      <div ref={contentRef} className="min-h-screen bg-[#121212] text-white py-16 px-4 sm:px-6 lg:px-8">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="max-w-4xl mx-auto"
        >
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold mb-6 bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent">
              Politique de Protection des Données
              <span className="block text-xl mt-2 bg-gradient-to-r from-yellow-500 to-yellow-600 bg-clip-text">
                Conformité RGPD
              </span>
            </h1>
          </div>
          
          <div className="bg-gray-900/60 backdrop-blur-sm rounded-xl p-8 shadow-xl border border-gray-800">
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2, duration: 0.5 }}
              className="space-y-8"
            >
              <div className="flex items-start gap-4">
                <Database className="w-8 h-8 text-yellow-500 flex-shrink-0 mt-1" />
                <div>
                  <h2 className="text-2xl font-semibold text-yellow-500 mb-4">1. Collecte des données</h2>
                  <p className="text-gray-300 mb-4">
                    Nous collectons les données suivantes lors de votre utilisation de nos services :
                  </p>
                  <ul className="list-disc pl-6 mb-6 text-gray-300 space-y-2">
                    <li>Nom et prénom</li>
                    <li>Adresse email</li>
                    <li>Numéro de téléphone</li>
                    <li>Informations de réservation</li>
                  </ul>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <Eye className="w-8 h-8 text-yellow-500 flex-shrink-0 mt-1" />
                <div>
                  <h2 className="text-2xl font-semibold text-yellow-500 mb-4">2. Utilisation des données</h2>
                  <p className="text-gray-300 mb-4">
                    Vos données sont utilisées pour :
                  </p>
                  <ul className="list-disc pl-6 mb-6 text-gray-300 space-y-2">
                    <li>Gérer vos réservations</li>
                    <li>Vous contacter concernant vos services</li>
                    <li>Améliorer nos services</li>
                    <li>Respecter nos obligations légales</li>
                  </ul>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <Lock className="w-8 h-8 text-yellow-500 flex-shrink-0 mt-1" />
                <div>
                  <h2 className="text-2xl font-semibold text-yellow-500 mb-4">3. Protection des données</h2>
                  <p className="text-gray-300 mb-4">
                    Nous mettons en œuvre des mesures de sécurité appropriées pour protéger vos données personnelles contre tout accès, modification, divulgation ou destruction non autorisée.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <UserCheck className="w-8 h-8 text-yellow-500 flex-shrink-0 mt-1" />
                <div>
                  <h2 className="text-2xl font-semibold text-yellow-500 mb-4">4. Vos droits</h2>
                  <p className="text-gray-300 mb-4">
                    Conformément au RGPD, vous disposez des droits suivants :
                  </p>
                  <ul className="list-disc pl-6 mb-6 text-gray-300 space-y-2">
                    <li>Droit d'accès à vos données</li>
                    <li>Droit de rectification</li>
                    <li>Droit à l'effacement</li>
                    <li>Droit à la limitation du traitement</li>
                    <li>Droit à la portabilité des données</li>
                    <li>Droit d'opposition</li>
                  </ul>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <Shield className="w-8 h-8 text-yellow-500 flex-shrink-0 mt-1" />
                <div>
                  <h2 className="text-2xl font-semibold text-yellow-500 mb-4">5. Contact</h2>
                  <p className="text-gray-300 mb-4">
                    Pour toute question concernant notre politique de protection des données ou pour exercer vos droits, veuillez nous contacter à l'adresse suivante : contact@elitetransfert.com
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </>
  );
}
